import React, { useState, useEffect } from 'react';

const PaymentForm = ({ sponsorshipId, onPaymentComplete }) => {
  const [paymentData, setPaymentData] = useState({
    amount: 0,
    payment_method: 'card',
    notes: ''
  });
  const [sponsorship, setSponsorship] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    fetchSponsorshipInfo();
  }, [sponsorshipId]);

  const fetchSponsorshipInfo = async () => {
    try {
      const response = await fetch(`/api/v1/sponsorships/${sponsorshipId}`, {
        headers: {
          'X-CSRF-Token': document.querySelector('[name="csrf-token"]').content
        }
      });

      const data = await response.json();

      if (response.ok) {
        setSponsorship(data.data.sponsorship);
        // Définir le montant par défaut basé sur le niveau
        const defaultAmounts = {
          sympathisant: 20,
          participant: 35,
          actif: 50
        };
        setPaymentData(prev => ({
          ...prev,
          amount: defaultAmounts[data.data.sponsorship.requested_level] || 35
        }));
      } else {
        setError(data.message || 'Erreur lors du chargement');
      }
    } catch (err) {
      setError('Erreur de connexion');
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setPaymentData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    // Validation côté client
    if (paymentData.amount <= 0) {
      setError('Le montant doit être supérieur à 0');
      setLoading(false);
      return;
    }

    try {
      const response = await fetch(`/api/v1/sponsorships/${sponsorshipId}/process_payment`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': document.querySelector('[name="csrf-token"]').content
        },
        body: JSON.stringify({
          payment: paymentData
        })
      });

      const data = await response.json();

      if (response.ok) {
        setSuccess(true);
        if (onPaymentComplete) {
          onPaymentComplete(data.data);
        }
      } else {
        setError(data.message || 'Erreur lors du traitement du paiement');
      }
    } catch (err) {
      setError('Erreur de connexion');
    } finally {
      setLoading(false);
    }
  };

  const getLevelLabel = (level) => {
    const labels = {
      sympathisant: 'Adhérent Sympathisant',
      participant: 'Adhérent Participant',
      actif: 'Adhérent Actif'
    };
    return labels[level] || level;
  };

  const getRecommendedAmount = (level) => {
    const amounts = {
      sympathisant: 20,
      participant: 35,
      actif: 50
    };
    return amounts[level] || 35;
  };

  if (success) {
    return (
      <div className="card">
        <div className="card-body text-center">
          <div className="mb-4">
            <i className="fas fa-check-circle fa-3x text-success"></i>
          </div>
          <h4 className="text-success">Paiement effectué !</h4>
          <p className="text-muted">
            Votre cotisation a été enregistrée avec succès. 
            Le processus d'adhésion est maintenant terminé.
          </p>
          <button 
            className="btn btn-primary"
            onClick={() => window.location.reload()}
          >
            Continuer
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="card">
      <div 
        className="card-header text-white"
        style={{ backgroundColor: '#1E4F8A' }}
      >
        <h5 className="mb-0">
          <i className="fas fa-credit-card me-2"></i>
          Paiement de la Cotisation
        </h5>
      </div>
      <div className="card-body">
        {error && (
          <div className="alert alert-danger" role="alert">
            {error}
          </div>
        )}

        {sponsorship && (
          <div className="alert alert-info mb-4">
            <h6 className="alert-heading">Informations sur votre adhésion</h6>
            <hr />
            <div className="row">
              <div className="col-md-6">
                <strong>Niveau d'adhésion:</strong><br />
                {getLevelLabel(sponsorship.requested_level)}
              </div>
              <div className="col-md-6">
                <strong>Montant recommandé:</strong><br />
                {getRecommendedAmount(sponsorship.requested_level)}€ / an
              </div>
            </div>
          </div>
        )}

        <form onSubmit={handleSubmit}>
          {/* Montant */}
          <div className="mb-3">
            <label htmlFor="amount" className="form-label">
              Montant de la cotisation (€) <span className="text-danger">*</span>
            </label>
            <div className="input-group">
              <input
                type="number"
                className="form-control"
                id="amount"
                name="amount"
                value={paymentData.amount}
                onChange={handleInputChange}
                min="1"
                step="0.01"
                required
              />
              <span className="input-group-text">€</span>
            </div>
            {sponsorship && (
              <small className="form-text text-muted">
                Montant recommandé pour {getLevelLabel(sponsorship.requested_level)}: 
                {getRecommendedAmount(sponsorship.requested_level)}€
              </small>
            )}
          </div>

          {/* Méthode de paiement */}
          <div className="mb-3">
            <label className="form-label">Méthode de paiement</label>
            <div className="row">
              <div className="col-md-6">
                <div className="form-check">
                  <input
                    className="form-check-input"
                    type="radio"
                    name="payment_method"
                    id="card"
                    value="card"
                    checked={paymentData.payment_method === 'card'}
                    onChange={handleInputChange}
                  />
                  <label className="form-check-label" htmlFor="card">
                    <i className="fas fa-credit-card me-2"></i>
                    Carte bancaire
                  </label>
                </div>
              </div>
              <div className="col-md-6">
                <div className="form-check">
                  <input
                    className="form-check-input"
                    type="radio"
                    name="payment_method"
                    id="transfer"
                    value="transfer"
                    checked={paymentData.payment_method === 'transfer'}
                    onChange={handleInputChange}
                  />
                  <label className="form-check-label" htmlFor="transfer">
                    <i className="fas fa-university me-2"></i>
                    Virement bancaire
                  </label>
                </div>
              </div>
            </div>
          </div>

          {/* Informations de paiement en fonction de la méthode */}
          {paymentData.payment_method === 'card' && (
            <div className="mb-3">
              <div className="alert alert-warning">
                <i className="fas fa-info-circle me-2"></i>
                <strong>Paiement par carte:</strong> Vous serez redirigé vers notre processeur 
                de paiement sécurisé pour finaliser la transaction.
              </div>
            </div>
          )}

          {paymentData.payment_method === 'transfer' && (
            <div className="mb-3">
              <div className="alert alert-info">
                <h6 className="alert-heading">
                  <i className="fas fa-university me-2"></i>
                  Informations pour le virement
                </h6>
                <hr />
                <p><strong>Bénéficiaire:</strong> Association Bouillon de Copains</p>
                <p><strong>IBAN:</strong> FR76 1234 5678 9012 3456 789A BIC</p>
                <p><strong>BIC:</strong> ABCDEFGH</p>
                <p className="mb-0">
                  <strong>Référence:</strong> COT-{sponsorshipId}-{new Date().getFullYear()}
                </p>
              </div>
            </div>
          )}

          {/* Notes */}
          <div className="mb-4">
            <label htmlFor="notes" className="form-label">
              Notes ou commentaires (optionnel)
            </label>
            <textarea
              className="form-control"
              id="notes"
              name="notes"
              rows="3"
              value={paymentData.notes}
              onChange={handleInputChange}
              placeholder="Ajoutez des notes concernant votre paiement..."
            />
          </div>

          {/* Récapitulatif */}
          <div className="card bg-light mb-4">
            <div className="card-body">
              <h6 className="card-title" style={{ color: '#00256C' }}>
                Récapitulatif
              </h6>
              <div className="row">
                <div className="col-6">
                  <strong>Montant:</strong>
                </div>
                <div className="col-6 text-end">
                  {paymentData.amount}€
                </div>
              </div>
              <div className="row">
                <div className="col-6">
                  <strong>Méthode:</strong>
                </div>
                <div className="col-6 text-end">
                  {paymentData.payment_method === 'card' ? 'Carte bancaire' : 'Virement bancaire'}
                </div>
              </div>
              <hr />
              <div className="row">
                <div className="col-6">
                  <strong>Total à payer:</strong>
                </div>
                <div className="col-6 text-end">
                  <strong>{paymentData.amount}€</strong>
                </div>
              </div>
            </div>
          </div>

          {/* Bouton de soumission */}
          <div className="d-grid">
            <button
              type="submit"
              className="btn btn-lg"
              style={{ 
                backgroundColor: '#1E4F8A',
                borderColor: '#1E4F8A',
                color: 'white'
              }}
              disabled={loading}
            >
              {loading ? (
                <>
                  <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                  Traitement en cours...
                </>
              ) : (
                <>
                  <i className="fas fa-credit-card me-2"></i>
                  {paymentData.payment_method === 'card' 
                    ? 'Procéder au paiement' 
                    : 'Confirmer le virement'}
                </>
              )}
            </button>
          </div>

          {paymentData.payment_method === 'transfer' && (
            <div className="mt-3">
              <small className="text-muted">
                * Pour un virement bancaire, votre adhésion sera validée après réception du paiement.
                Vous recevrez une confirmation par email.
              </small>
            </div>
          )}
        </form>
      </div>
    </div>
  );
};

export default PaymentForm;